import { useState, useEffect, useRef } from 'react';
import { searchService } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';

const AgencySearchModal = ({ isOpen, onClose, onAgencySelect, title = "Search Agencies" }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedAgency, setSelectedAgency] = useState(null);
  
  const searchInputRef = useRef(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Search agencies when query changes
  useEffect(() => {
    if (debouncedSearchQuery.trim() && isOpen) {
      searchAgencies(debouncedSearchQuery, 1);
      setCurrentPage(1);
    } else if (!debouncedSearchQuery.trim()) {
      setSearchResults([]);
      setPagination(null);
      setIsSearching(false);
    }
  }, [debouncedSearchQuery, isOpen]);

  const searchAgencies = async (query, page = 1) => {
    setIsSearching(true);
    try {
      const params = {
        type: 'agencies',
        query: query,
        per_page: 10,
        page: page
      };

      const response = await searchService.search(params);
      
      if (response.success && response.data) {
        // Updated to handle new response structure
        const agencies = response.data.results?.agencies || [];
        setSearchResults(agencies);
        setPagination(response.pagination || null);
      } else {
        setSearchResults([]);
        setPagination(null);
      }
    } catch (error) {
      console.error('Agency search error:', error);
      setSearchResults([]);
      setPagination(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePageChange = (page) => {
    if (searchQuery.trim()) {
      setCurrentPage(page);
      searchAgencies(searchQuery, page);
    }
  };

  const handleAgencySelect = (agency) => {
    setSelectedAgency(agency);
    onClose();
    onAgencySelect(agency);
  };

  const handleOpenSearch = () => {
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  useEffect(() => {
    if (isOpen) {
      handleOpenSearch();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
        {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold text-[#202630]">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Search Input */}
          <div className="p-6 border-b">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Enter agency name to search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 pl-12 pr-4 border border-[#E9E9E9] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg className="w-5 h-5 text-[#626262] absolute left-4 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {isSearching && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                </div>
              )}
            </div>
          </div>

          {/* Search Results */}
          <div className="flex-1 overflow-y-auto p-6">
            {isSearching && searchResults.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-center">
                  <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                  <p className="text-secondary">Searching agencies...</p>
                </div>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="space-y-4">
                {/* Results List */}
                <div className="space-y-2">
                  {searchResults.map((agency) => (
                    <button
                      key={agency.id}
                      type="button"
                      onClick={() => handleAgencySelect(agency)}
                      className="w-full p-4 border border-[#E9E9E9] rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0">
                          {agency.logo_url && (
                            <img 
                              src={agency.logo_url} 
                              alt={agency.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="text-base font-medium text-[#1A212F] truncate">
                              {agency.name}
                            </h3>
                            {/* D-A follow-up (Marin, 2026-08-04):
                                has_fifa_license is self-declared — the chip
                                says "Licensed", never "FIFA" (that wording
                                is reserved for the verified agent badge). */}
                            {agency.has_fifa_license && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex-shrink-0">
                                Licensed
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            {agency.email && (
                              <span className="truncate">{agency.email}</span>
                            )}
                            
                            {agency.total_agents_count && (
                              <span className="flex-shrink-0">{agency.total_agents_count} agents</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Pagination */}
                {pagination && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-secondary">
                        Showing {searchResults.length} results
                      </div>
                      
                      {(pagination.total_pages > 1 || pagination.has_more) && (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage <= 1}
                            className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Previous
                          </button>
                          
                          <span className="px-2 text-sm text-secondary">
                            {currentPage}
                          </span>
                          
                          <button
                            type="button"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={!pagination.has_more && currentPage >= pagination.total_pages}
                            className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : searchQuery.trim() && !isSearching ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-center">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-secondary">No agencies found for "{searchQuery}"</p>
                  <p className="text-sm text-gray-400 mt-1">Try adjusting your search terms</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32">
                <div className="text-center">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-secondary">Start typing to search for agencies</p>
                  <p className="text-sm text-gray-400 mt-1">Enter agency name to find and join</p>
                </div>
              </div>
            )}
          </div>
        </div>
    </div>
  );
};

export default AgencySearchModal; 